import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Package, Heart, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-concrete-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-chicago-red border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-concrete-white" data-testid="profile-page">
      {/* Header */}
      <div className="bg-deep-obsidian py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="font-bebas text-chicago-red text-lg tracking-widest">MY ACCOUNT</p>
          <h1 className="font-anton text-4xl md:text-5xl text-white tracking-tighter uppercase">
            {user?.name}
          </h1>
          <p className="text-cement-grey mt-2">{user?.email}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 p-6 sticky top-24">
              <nav className="space-y-2">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-4 py-3 bg-gray-100 text-deep-obsidian font-bold rounded-sm"
                >
                  <Package className="w-5 h-5" />
                  Order History
                </Link>
                <Link
                  to="/wishlist"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-cement-grey hover:text-deep-obsidian transition-colors rounded-sm"
                >
                  <Heart className="w-5 h-5" />
                  Wishlist
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-cement-grey hover:text-chicago-red transition-colors w-full text-left rounded-sm"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <h2 className="font-bebas text-2xl tracking-wider mb-6">ORDER HISTORY</h2>

            {orders.length === 0 ? (
              <div className="bg-white border border-gray-200 p-12 text-center">
                <Package className="w-12 h-12 text-cement-grey mx-auto mb-4" />
                <p className="font-bebas text-xl text-cement-grey">NO ORDERS YET</p>
                <p className="text-cement-grey mt-2">Start shopping to see your orders here</p>
                <Link to="/products">
                  <Button className="mt-6 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
                    Shop Now
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white border border-gray-200 overflow-hidden"
                    data-testid={`order-${order.id}`}
                  >
                    {/* Order Header */}
                    <div className="bg-gray-50 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="text-xs text-cement-grey uppercase tracking-wider">Order ID</p>
                        <p className="font-bold">{order.id.slice(0, 8)}...</p>
                      </div>
                      <div>
                        <p className="text-xs text-cement-grey uppercase tracking-wider">Date</p>
                        <p className="font-bold">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-cement-grey uppercase tracking-wider">Total</p>
                        <p className="font-bebas text-xl">${order.total?.toFixed(2)}</p>
                      </div>
                      <div>
                        <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${
                          order.status === 'confirmed' 
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'shipped'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="p-6 space-y-4">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex gap-4 items-center">
                          <div className="w-16 h-16 bg-gray-100 flex-shrink-0">
                            <img
                              src={item.image || 'https://via.placeholder.com/64'}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold">{item.name}</p>
                            <p className="text-cement-grey text-sm">Size: {item.selected_size} • Qty: {item.quantity}</p>
                          </div>
                          <p className="font-bebas text-lg">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
